import { OrchidDriveEntryKeys, UploadEntry, FileEntry } from "../types";

type OrchidDriveFileUploadStatusReducerResetAction = {
  type: "reset";
};

type OrchidDriveFileUploadStatusReducerUploadNewAction = {
  type: "upload_new";
  uploadEntry: UploadEntry;
  totalExpectedSize: number;
};

type OrchidDriveFileUploadStatusReducerUploadTaskCreatedAction = {
  type: "upload_task_created";
  uploadEntry: UploadEntry;
  uploadTaskId: string | null;
};

type OrchidDriveFileUploadStatusReducerUploadTaskCreateFailedAction = {
  type: "upload_task_create_failed";
  uploadEntry: UploadEntry;
};

type OrchidDriveFileUploadStatusReducerDirectoryCreatedAction = {
  type: "directory_created";
  uploadEntry: UploadEntry;
  fileEntry: FileEntry;
  createdDirectoryName: string;
  createdDirectoryId: string;
};

type OrchidDriveFileUploadStatusReducerDirectoryCreateFailedAction = {
  type: "directory_create_failed";
  uploadEntry: UploadEntry;
  fileEntry: FileEntry;
};

type OrchidDriveFileUploadStatusReducerFileEntryCreatedAction = {
  type: "file_entry_created";
  uploadEntry: UploadEntry;
  fileEntry: FileEntry;
  fileDataId: string;
  cipherKeys: OrchidDriveEntryKeys;
  chunkSize: number;
};

type OrchidDriveFileUploadStatusReducerFileEntryCreateFailedAction = {
  type: "file_entry_create_failed";
  uploadEntry: UploadEntry;
  fileEntry: FileEntry;
};

type OrchidDriveFileUploadStatusReducerUploadChunkProgressAction = {
  type: "upload_chunk_progress";
  fileDataId: string;
  lastUploadedFileChunkNum: number;
  completed: boolean;
};

type OrchidDriveFileUploadStatusReducerUploadChunkFailedAction = {
  type: "upload_chunk_failed";
  fileDataId: string;
};

type OrchidDriveFileUploadStatusReducerUploadAbortAction = {
  type: "upload_abort";
  uploadEntry: UploadEntry;
};

type OrchidDriveFileUploadStatusReducerUploadAbortByTaskIdAction = {
  type: "upload_abort_by_task_id";
  uploadTaskId: string;
};

type OrchidDriveFileUploadStatusReducerFinalizeUploadAction = {
  type: "finalize_upload";
  uploadTaskId: string;
};

type OrchidDriveFileUploadStatusReducerFinalizeUploadFailedAction = {
  type: "finalize_upload_failed";
  uploadTaskId: string;
};

type FileEntryInfo = {
  fileDataId: string;
  cipherKeys: OrchidDriveEntryKeys;
  chunkSize: number;
  lastUploadedFileChunkNum: null | number;
};

type ParentDirectoryToCreate =
  | {
      type: "parent_directory_to_create";
      baseDirectoryId: string | null;
      pathToCreate: string[];
    }
  | {
      type: "parent_directory_available";
      parentDirectoryId: string | null;
    };

type FileUploadProgress = {
  uploaded: boolean;
  parentDirectory: ParentDirectoryToCreate;
  fileEntryInfo: null | FileEntryInfo;
};

type CurrentCreateDirectory = {
  uploadEntry: UploadEntry;
  uploadTaskId: string;
  fileEntry: FileEntry;
  baseDirectoryId: string | null;
  pathToCreate: string[];
  retryCount: number;
};

type DriveUploadEntry = {
  uploadEntry: UploadEntry;
  totalExpectedSize: number;
  uploadTaskId: string | null;
  fileUploadProgress: Map<FileEntry, FileUploadProgress>;
};

type OrchidDriveFileUploadStatusState = {
  currentCreateUploadTask: null | {
    uploadEntry: UploadEntry;
    totalExpectedSize: number;
    retryCount: number;
  };
  currentCreateDirectory: null | CurrentCreateDirectory;
  currentFileEntryToCreate: null | {
    uploadEntry: UploadEntry;
    uploadTaskId: string;
    fileEntry: FileEntry;
    parentDirectoryId: string | null;
    retryCount: number;
  };
  currentUploadChunk: null | {
    uploadEntry: UploadEntry;
    uploadTaskId: string;
    fileEntry: FileEntry;
    parentDirectoryId: string | null;
    fileEntryInfo: FileEntryInfo;
    retryCount: number;
  };
  driveUploadEntries: Array<DriveUploadEntry>;
  finishedUploadTaskIds: {
    uploadTaskIds: string[];
    retryCount: number;
  };
};

function omitMatchingUploadEntry<T extends { uploadEntry: UploadEntry }>(
  entry: null | T,
  uploadEntry: UploadEntry,
): null | T {
  return entry?.uploadEntry === uploadEntry ? null : entry;
}

function isUploadFinished(
  uploadEntry: UploadEntry,
  fileUploadProgress: Map<FileEntry, FileUploadProgress>,
): boolean {
  for (const fileUploadEntry of uploadEntry.files) {
    const entry = fileUploadProgress.get(fileUploadEntry.file);
    if (!entry || !entry.uploaded) return false;
  }
  return true;
}

function updateCurrentCreateDirectory(
  currentCreateDirectory: null | CurrentCreateDirectory,
  action: OrchidDriveFileUploadStatusReducerDirectoryCreatedAction,
): null | CurrentCreateDirectory {
  if (
    currentCreateDirectory?.uploadEntry !== action.uploadEntry ||
    currentCreateDirectory?.fileEntry !== action.fileEntry ||
    currentCreateDirectory?.pathToCreate[0] !== action.createdDirectoryName
  ) {
    return currentCreateDirectory;
  }

  return currentCreateDirectory.pathToCreate.length === 1
    ? null
    : {
        ...currentCreateDirectory,
        baseDirectoryId: action.createdDirectoryId,
        pathToCreate: currentCreateDirectory.pathToCreate.slice(1),
      };
}

function updateParentDirectory(
  parentDirectory: ParentDirectoryToCreate,
  action: OrchidDriveFileUploadStatusReducerDirectoryCreatedAction,
): ParentDirectoryToCreate {
  if (
    parentDirectory.type !== "parent_directory_to_create" ||
    parentDirectory.pathToCreate[0] !== action.createdDirectoryName
  ) {
    return parentDirectory;
  }

  return parentDirectory.pathToCreate.length === 1
    ? {
        type: "parent_directory_available",
        parentDirectoryId: action.createdDirectoryId,
      }
    : {
        type: "parent_directory_to_create",
        baseDirectoryId: action.createdDirectoryId,
        pathToCreate: parentDirectory.pathToCreate.slice(1),
      };
}

function scheduleNextTask(
  state: OrchidDriveFileUploadStatusState,
): OrchidDriveFileUploadStatusState {
  if (
    state.driveUploadEntries.length === 0 ||
    state.currentCreateUploadTask ||
    state.currentCreateDirectory ||
    state.currentFileEntryToCreate ||
    state.currentUploadChunk
  ) {
    return state;
  }

  const uploadEntry = state.driveUploadEntries[0];

  if (!uploadEntry.uploadTaskId) {
    return {
      ...state,
      currentCreateUploadTask: {
        uploadEntry: uploadEntry.uploadEntry,
        totalExpectedSize: uploadEntry.totalExpectedSize,
        retryCount: 0,
      },
    };
  }

  for (const fileEntry of uploadEntry.uploadEntry.files) {
    const fileUploadProgress = uploadEntry.fileUploadProgress.get(
      fileEntry.file,
    );
    if (!fileUploadProgress) {
      throw new Error(
        "file upload progress not found, this should never happen",
      );
    }

    if (fileUploadProgress.uploaded) continue;

    if (
      fileUploadProgress.parentDirectory.type === "parent_directory_to_create"
    ) {
      return {
        ...state,
        currentCreateDirectory: {
          uploadEntry: uploadEntry.uploadEntry,
          uploadTaskId: uploadEntry.uploadTaskId,
          fileEntry: fileEntry.file,
          baseDirectoryId: fileUploadProgress.parentDirectory.baseDirectoryId,
          pathToCreate: fileUploadProgress.parentDirectory.pathToCreate,
          retryCount: 0,
        },
      };
    }

    if (!fileUploadProgress.fileEntryInfo) {
      return {
        ...state,
        currentFileEntryToCreate: {
          uploadEntry: uploadEntry.uploadEntry,
          uploadTaskId: uploadEntry.uploadTaskId,
          fileEntry: fileEntry.file,
          parentDirectoryId:
            fileUploadProgress.parentDirectory.parentDirectoryId,
          retryCount: 0,
        },
      };
    }

    return {
      ...state,
      currentUploadChunk: {
        uploadEntry: uploadEntry.uploadEntry,
        uploadTaskId: uploadEntry.uploadTaskId,
        fileEntry: fileEntry.file,
        parentDirectoryId: fileUploadProgress.parentDirectory.parentDirectoryId,
        fileEntryInfo: fileUploadProgress.fileEntryInfo,
        retryCount: 0,
      },
    };
  }

  throw new Error("no file to upload found, this should never happen");
}

export default function OrchidDriveFileUploadStatusReducer(
  state: OrchidDriveFileUploadStatusState,
  action:
    | OrchidDriveFileUploadStatusReducerResetAction
    | OrchidDriveFileUploadStatusReducerUploadNewAction
    | OrchidDriveFileUploadStatusReducerUploadTaskCreatedAction
    | OrchidDriveFileUploadStatusReducerUploadTaskCreateFailedAction
    | OrchidDriveFileUploadStatusReducerDirectoryCreatedAction
    | OrchidDriveFileUploadStatusReducerDirectoryCreateFailedAction
    | OrchidDriveFileUploadStatusReducerFileEntryCreatedAction
    | OrchidDriveFileUploadStatusReducerFileEntryCreateFailedAction
    | OrchidDriveFileUploadStatusReducerUploadChunkProgressAction
    | OrchidDriveFileUploadStatusReducerUploadChunkFailedAction
    | OrchidDriveFileUploadStatusReducerUploadAbortAction
    | OrchidDriveFileUploadStatusReducerUploadAbortByTaskIdAction
    | OrchidDriveFileUploadStatusReducerFinalizeUploadAction
    | OrchidDriveFileUploadStatusReducerFinalizeUploadFailedAction,
): OrchidDriveFileUploadStatusState {
  switch (action.type) {
    case "reset": {
      if (
        state.currentCreateUploadTask === null &&
        state.currentCreateDirectory === null &&
        state.currentFileEntryToCreate === null &&
        state.currentUploadChunk === null &&
        state.driveUploadEntries.length === 0 &&
        state.finishedUploadTaskIds.uploadTaskIds.length === 0
      ) {
        return state;
      }
      return {
        currentCreateUploadTask: null,
        currentFileEntryToCreate: null,
        currentCreateDirectory: null,
        currentUploadChunk: null,
        driveUploadEntries: [],
        finishedUploadTaskIds: {
          uploadTaskIds: [],
          retryCount: 0,
        },
      };
    }

    case "upload_new": {
      const fileUploadProgress: Map<FileEntry, FileUploadProgress> = new Map();

      // sanity check
      if (action.uploadEntry.files.length === 0) return state;

      // sanity check
      for (const entries of state.driveUploadEntries) {
        for (const fileInfo of action.uploadEntry.files) {
          if (entries.fileUploadProgress.has(fileInfo.file)) {
            throw new Error(
              `FileEntry={name: ${fileInfo.file.name}, pathToCreate: ${fileInfo.pathToCreate}} already belongs to an existing uploadTask: {uploadTaskId: ${entries.uploadTaskId}}}. This should never happen.`,
            );
          }
        }
      }

      for (const fileInfo of action.uploadEntry.files) {
        fileUploadProgress.set(fileInfo.file, {
          uploaded: false,
          parentDirectory:
            !fileInfo.pathToCreate || fileInfo.pathToCreate.length === 0
              ? {
                  type: "parent_directory_available",
                  parentDirectoryId: fileInfo.baseDirectoryId,
                }
              : {
                  type: "parent_directory_to_create",
                  baseDirectoryId: fileInfo.baseDirectoryId,
                  pathToCreate: fileInfo.pathToCreate,
                },
          fileEntryInfo: null,
        });
      }

      return scheduleNextTask({
        ...state,
        driveUploadEntries: [
          ...state.driveUploadEntries,
          {
            uploadEntry: action.uploadEntry,
            uploadTaskId: null,
            totalExpectedSize: action.totalExpectedSize,
            fileUploadProgress,
          },
        ],
      });
    }

    case "upload_task_created": {
      const uploadEntry = state.driveUploadEntries.find(
        (e) => e.uploadEntry === action.uploadEntry,
      );
      if (!uploadEntry) {
        // looks like we don't currently have this upload entry
        return state;
      }

      return scheduleNextTask({
        ...state,
        currentCreateUploadTask:
          action.uploadEntry === state.currentCreateUploadTask?.uploadEntry
            ? null
            : state.currentCreateUploadTask,
        driveUploadEntries: [
          {
            ...uploadEntry,
            uploadTaskId: action.uploadTaskId,
          },
          ...state.driveUploadEntries.filter((e) => e !== uploadEntry),
        ],
      });
    }

    case "upload_task_create_failed": {
      if (state?.currentCreateUploadTask?.uploadEntry !== action.uploadEntry) {
        // we are not currently working on this
        return state;
      }
      return {
        ...state,
        currentCreateUploadTask: {
          ...state.currentCreateUploadTask,
          retryCount: state.currentCreateUploadTask.retryCount + 1,
        },
      };
    }

    case "directory_created": {
      // we are done creating directories
      const uploadEntry = state.driveUploadEntries.find(
        (e) => e.uploadEntry === action.uploadEntry,
      );
      if (!uploadEntry) {
        // looks like we don't currently have this upload entry
        return state;
      }

      const fileUpload = uploadEntry.fileUploadProgress.get(action.fileEntry);
      if (!fileUpload) {
        throw new Error(
          "can't find file upload entry, this should never happen",
        );
      }

      const fileUploadProgress = new Map(uploadEntry.fileUploadProgress);
      fileUploadProgress.set(action.fileEntry, {
        ...fileUpload,
        parentDirectory: updateParentDirectory(
          fileUpload.parentDirectory,
          action,
        ),
      });

      return scheduleNextTask({
        ...state,
        currentCreateDirectory: updateCurrentCreateDirectory(
          state.currentCreateDirectory,
          action,
        ),
        driveUploadEntries: [
          {
            ...uploadEntry,
            fileUploadProgress,
          },
          ...state.driveUploadEntries.filter((e) => e !== uploadEntry),
        ],
      });
    }

    case "directory_create_failed": {
      if (
        state?.currentFileEntryToCreate?.fileEntry !== action.fileEntry ||
        state?.currentFileEntryToCreate?.uploadEntry !== action.uploadEntry
      ) {
        // we are not currently working on this
        return state;
      }
      return scheduleNextTask({
        ...state,
        currentFileEntryToCreate: {
          ...state.currentFileEntryToCreate,
          retryCount: state.currentFileEntryToCreate.retryCount + 1,
        },
      });
    }

    case "file_entry_created": {
      const uploadEntry = state.driveUploadEntries.find(
        (e) =>
          e.uploadEntry === action.uploadEntry &&
          e.fileUploadProgress.has(action.fileEntry),
      );
      // we no longer upload this apparently
      if (!uploadEntry) return state;

      const fileUpload = uploadEntry.fileUploadProgress.get(action.fileEntry);
      if (!fileUpload) {
        throw new Error(
          "can't find file upload entry, this should never happen",
        );
      }

      const fileEntryInfo: FileEntryInfo = {
        fileDataId: action.fileDataId,
        cipherKeys: action.cipherKeys,
        chunkSize: action.chunkSize,
        lastUploadedFileChunkNum: null,
      };

      const fileUploadProgress = new Map(uploadEntry.fileUploadProgress);
      fileUploadProgress.set(action.fileEntry, {
        ...fileUpload,
        fileEntryInfo: fileEntryInfo,
      });

      return scheduleNextTask({
        ...state,
        currentFileEntryToCreate:
          state.currentFileEntryToCreate?.uploadEntry === action.uploadEntry &&
          state.currentFileEntryToCreate?.fileEntry === action.fileEntry
            ? null
            : state.currentFileEntryToCreate,
        driveUploadEntries: [
          {
            ...uploadEntry,
            fileUploadProgress,
          },
          ...state.driveUploadEntries.filter((e) => e !== uploadEntry),
        ],
      });
    }

    case "file_entry_create_failed": {
      if (
        state?.currentFileEntryToCreate?.fileEntry !== action.fileEntry ||
        state?.currentFileEntryToCreate?.uploadEntry !== action.uploadEntry
      ) {
        // we are not currently working on this
        return state;
      }
      return scheduleNextTask({
        ...state,
        currentFileEntryToCreate: {
          ...state.currentFileEntryToCreate,
          retryCount: state.currentFileEntryToCreate.retryCount + 1,
        },
      });
    }

    case "upload_chunk_progress": {
      if (
        state.currentUploadChunk?.fileEntryInfo?.fileDataId !==
        action.fileDataId
      ) {
        // we are not currently uploading that file
        return state;
      }

      if (
        !(
          (state.currentUploadChunk.fileEntryInfo.lastUploadedFileChunkNum ===
            null &&
            action.lastUploadedFileChunkNum === 0) ||
          (state.currentUploadChunk.fileEntryInfo.lastUploadedFileChunkNum !==
            null &&
            state.currentUploadChunk.fileEntryInfo.lastUploadedFileChunkNum +
              1 ===
              action.lastUploadedFileChunkNum)
        )
      ) {
        throw new Error(
          "unexpected chunk num uploaded, this should never happen",
        );
      }

      if (action.completed) {
        const currentUploadEntry = state.currentUploadChunk.uploadEntry;
        const currentFileEntry = state.currentUploadChunk.fileEntry;

        const uploadEntry = state.driveUploadEntries.find(
          (e) => e.uploadEntry === currentUploadEntry,
        );
        if (!uploadEntry) {
          throw new Error("can't find file entry, this should never happen");
        }
        const currentFileUploadInfo =
          uploadEntry.fileUploadProgress.get(currentFileEntry);
        if (!currentFileUploadInfo) {
          throw new Error(
            "can't find file upload entry, this should never happen",
          );
        }

        const fileUploadProgress = new Map(uploadEntry.fileUploadProgress);
        fileUploadProgress.set(currentFileEntry, {
          ...currentFileUploadInfo,
          uploaded: true,
        });

        if (isUploadFinished(uploadEntry.uploadEntry, fileUploadProgress)) {
          return scheduleNextTask({
            ...state,
            currentUploadChunk: null,
            driveUploadEntries: state.driveUploadEntries.filter(
              (e) => e !== uploadEntry,
            ),
            finishedUploadTaskIds: {
              ...state.finishedUploadTaskIds,
              uploadTaskIds: [
                ...state.finishedUploadTaskIds.uploadTaskIds,
                state.currentUploadChunk.uploadTaskId,
              ],
            },
          });
        }

        return scheduleNextTask({
          ...state,
          currentUploadChunk: null,
          driveUploadEntries: [
            {
              ...uploadEntry,
              fileUploadProgress,
            },
            ...state.driveUploadEntries.filter((e) => e !== uploadEntry),
          ],
        });
      } else {
        return scheduleNextTask({
          ...state,
          currentUploadChunk: {
            ...state.currentUploadChunk,
            retryCount: 0,
            fileEntryInfo: {
              ...state.currentUploadChunk.fileEntryInfo,
              lastUploadedFileChunkNum: action.lastUploadedFileChunkNum,
            },
          },
        });
      }
    }

    case "upload_chunk_failed": {
      if (
        state?.currentUploadChunk?.fileEntryInfo?.fileDataId !==
        action.fileDataId
      ) {
        // we are not currently uploading this file
        return state;
      }
      return scheduleNextTask({
        ...state,
        currentUploadChunk: {
          ...state.currentUploadChunk,
          retryCount: state.currentUploadChunk.retryCount + 1,
        },
      });
    }

    case "upload_abort": {
      return scheduleNextTask({
        ...state,
        currentCreateUploadTask: omitMatchingUploadEntry(
          state.currentCreateUploadTask,
          action.uploadEntry,
        ),
        currentCreateDirectory: omitMatchingUploadEntry(
          state.currentCreateDirectory,
          action.uploadEntry,
        ),
        currentFileEntryToCreate: omitMatchingUploadEntry(
          state.currentFileEntryToCreate,
          action.uploadEntry,
        ),
        currentUploadChunk: omitMatchingUploadEntry(
          state.currentUploadChunk,
          action.uploadEntry,
        ),
        driveUploadEntries: state.driveUploadEntries.filter(
          (e) => e.uploadEntry !== action.uploadEntry,
        ),
      });
    }

    case "upload_abort_by_task_id": {
      const uploadEntry = state.driveUploadEntries.find(
        (e) => e.uploadTaskId === action.uploadTaskId,
      )?.uploadEntry;
      if (!uploadEntry) {
        // upload entry is not currently being uploaded here
        return state;
      }
      return scheduleNextTask({
        ...state,
        currentCreateUploadTask: omitMatchingUploadEntry(
          state.currentCreateUploadTask,
          uploadEntry,
        ),
        currentCreateDirectory: omitMatchingUploadEntry(
          state.currentCreateDirectory,
          uploadEntry,
        ),
        currentFileEntryToCreate: omitMatchingUploadEntry(
          state.currentFileEntryToCreate,
          uploadEntry,
        ),
        currentUploadChunk: omitMatchingUploadEntry(
          state.currentUploadChunk,
          uploadEntry,
        ),
        driveUploadEntries: state.driveUploadEntries.filter(
          (e) => e.uploadEntry !== uploadEntry,
        ),
      });
    }

    case "finalize_upload": {
      return scheduleNextTask({
        ...state,
        finishedUploadTaskIds: {
          uploadTaskIds: state.finishedUploadTaskIds.uploadTaskIds.filter(
            (ut) => ut !== action.uploadTaskId,
          ),
          retryCount: 0,
        },
      });
    }
    case "finalize_upload_failed": {
      return scheduleNextTask({
        ...state,
        finishedUploadTaskIds: {
          ...state.finishedUploadTaskIds,
          retryCount: state.finishedUploadTaskIds.retryCount + 1,
        },
      });
    }
  }
  throw new Error(`unexpected action type: ${action}`);
}
